<style src="./Layer.scss" lang="scss"></style>
<template src="./Layer.html"></template>

<script>
  import Teaser from '@/components/Teaser/Teaser'
  import Button from '@/components/Button/Button'
  import Icon from '@/components/Icon/Icon'
  import Searchbar from '@/components/Searchbar/Searchbar'
  import { AdServiceWrapper } from '@/services/wrapper/adServiceWrapper'
  import { Helper } from '@/services/helper/helper'

  /**
   * The Layer component for the page.
   *
   * Currently only used for the inactivity layer.
   * The Layer can be enforced by setting the GET param _inactivityLayerDuration=X in the url, while X are the ms for the delay
   *
   * @vue-prop {Number} delay The initial delay after which time period the layer should be displayed
   * @vue-data {'accent' | 'primary'} buttonImportance The color of the button mapped by importance
   * @vue-data {Number} delayTime Internal time of the delay
   * @vue-data {Boolean} hiddenPermanent Determines whether the layer is permanently hidden or not
   * @vue-data {Object} timer Internal pointer to the timeout to show the layer
   * @vue-data {Boolean} true if the timer is stopped and needs to be reactivated
   * @vue-data {Object} urlParams The url parameters
   * @vue-data {Boolean} visible Determines whether the layer is visible or not
   * @listens Window#scroll
   */
  export default {
    props: {
      delay: {
        type: Number,
        default: 300000
      }
    },
    data () {
      return {
        buttonImportance: process.env.VUE_APP_BRAND === 'einsundeins' ? 'accent' : 'primary',
        visible: false,
        hiddenPermanent: false,
        delayTime: this.delay,
        timer: null,
        timerStopped: false,
        urlParams: new URLSearchParams(window.location.search)
      }
    },
    watch: {
      visible () {
        this.visible ? this.openLayer() : this.closeLayer()
      }
    },
    methods: {
      openLayer () {
        if (this.hiddenPermanent || this.timerStopped) {
          return
        }

        document.querySelector('body').setAttribute('data-inactivity-layer', '')

        this.$el.querySelector('[data-service-slot]').innerHTML = ''
        setTimeout(() => {
          AdServiceWrapper.makeAd('rectangle_layer')
        }, 50) // short timeout because otherwise the ad doesn't get loaded

        setTimeout(() => {
          this.$el.querySelector('input[data-type=search]')?.focus()
          Helper.fireEvent('layerOpened')
        }, 50)
      },
      closeLayer () {
        document.querySelector('body').removeAttribute('data-inactivity-layer')

        let event = new CustomEvent('layerClosed', {
          bubbles: true,
          cancelable: true,
          detail: { source: 'Layer.vue' }
        })
        document.body.dispatchEvent(event)

        if (!this.hiddenPermanent) {
          this.startTimer()
        }
      },
      hidePermanent () {
        localStorage.setItem('info-layer-home-inactive', '1')
        this.hiddenPermanent = true
        this.visible = false
      },
      reloadSite () {
        this.visible = false
        window.location.reload(false)
      },
      startTimer () {
        window.onload = this.resetTimer
        window.onmousemove = this.resetTimer
        window.onmousedown = this.resetTimer
        window.ontouchstart = this.resetTimer
        window.onclick = this.resetTimer
        window.onkeypress = this.resetTimer
        window.addEventListener('scroll', this.resetTimer, true)
      },
      checkLayerVisibility () {
        let layerContentWrapper = this.$el.querySelector('[data-layer-content-wrapper]')

        if (layerContentWrapper
          && (layerContentWrapper.offsetWidth > document.documentElement.clientWidth
            || layerContentWrapper.offsetHeight > document.documentElement.clientHeight)) {
          this.visible = false
          clearTimeout(this.timer)
        } else {
          this.resetTimer()
        }
      },
      resetTimer () {
        clearTimeout(this.timer)

        if (this.hiddenPermanent || this.timerStopped) {
          return
        }

        const _this = this
        this.timer = setTimeout(() => {
          let layerContentWrapper = _this.$el.querySelector('[data-layer-content-wrapper]')

          if (layerContentWrapper
            && (layerContentWrapper.offsetWidth > document.documentElement.clientWidth
              || layerContentWrapper.offsetHeight > document.documentElement.clientHeight)) {
            _this.visible = false
            clearTimeout(_this.timer)
          } else {
            _this.visible = true
          }
        }, this.delayTime)
      },
      stopTimer () {
        this.timerStopped = true
        this.visible = false
        clearTimeout(this.timer)
      },
      restartTimer () {
        this.timerStopped = false
        this.resetTimer()
      }
    },
    mounted () {
      let layerOverwriteTime = +this.urlParams?.get('_inactivityLayerDuration') || 0

      if (layerOverwriteTime > 0) {
        this.delayTime = layerOverwriteTime
      } else if (localStorage.getItem('info-layer-home-inactive') === '1') {
        this.hiddenPermanent = true
        return
      }

      this.startTimer()
      window.addEventListener('resize', this.checkLayerVisibility)
      document.addEventListener('pppLayerVisible', this.stopTimer)
      document.addEventListener('pppLayerClosed', this.restartTimer)
    },
    components: {
      'hp-teaser': Teaser,
      'hp-button': Button,
      'hp-icon': Icon,
      'hp-searchbar': Searchbar
    }
  }
</script>
